import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  ArrowLeft, Send, Camera, ImagePlus, X, Loader2, MessageSquare, Flag, Lock, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../../components/ui/textarea";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";

const ROLE_TONE = {
  buyer: "text-indigo-700 bg-indigo-50",
  supplier: "text-emerald-700 bg-emerald-50",
  employee: "text-amber-700 bg-amber-50",
};

const ROLE_LABEL = { buyer: "Buyer", supplier: "Supplier", employee: "Employee" };

const CLOSED_BANNER = {
  completed: { text: "Order Completed – Chat Closed", tone: "bg-slate-100 text-slate-600" },
  cancelled_completed: { text: "Order Cancelled – Chat Closed", tone: "bg-slate-100 text-slate-600" },
  issue_resolved: { text: "Issue Resolved • Delivery Completed", tone: "bg-emerald-50 text-emerald-700" },
};

function timeLabel(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

/** Section 1 — tapping the camera icon offers "Take Photo" (forces the
 * device camera via capture="environment") and "Upload Image" (gallery/
 * files, no capture attribute) as two explicit options — never silently
 * opens the gallery when Camera was requested. Shared by the message
 * composer and the Raise Issue composer. */
function ImageSourceSheet({ open, onClose, onFile }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  if (!open) return null;
  const pick = (file) => { onClose(); if (file) onFile(file); };
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end justify-center" onClick={onClose} data-testid="image-source-sheet">
      <div className="bg-white w-full max-w-md rounded-t-3xl p-4 pb-6 space-y-2" onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-2" />
        <button
          onClick={() => cameraRef.current?.click()}
          data-testid="image-source-camera"
          className="w-full h-13 rounded-2xl bg-slate-50 hover:bg-slate-100 flex items-center gap-3 px-4 py-3.5 font-semibold text-slate-800 transition"
        >
          <Camera className="w-5 h-5 text-emerald-600" /> Take Photo
        </button>
        <button
          onClick={() => galleryRef.current?.click()}
          data-testid="image-source-upload"
          className="w-full h-13 rounded-2xl bg-slate-50 hover:bg-slate-100 flex items-center gap-3 px-4 py-3.5 font-semibold text-slate-800 transition"
        >
          <ImagePlus className="w-5 h-5 text-indigo-600" /> Upload Image
        </button>
        <button onClick={onClose} data-testid="image-source-cancel" className="w-full h-11 rounded-2xl text-slate-500 font-semibold">
          Cancel
        </button>
        <input
          ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pick(f); }}
          data-testid="image-source-camera-input"
        />
        <input
          ref={galleryRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pick(f); }}
          data-testid="image-source-gallery-input"
        />
      </div>
    </div>
  );
}

export default function OrderChat() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [chatState, setChatState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // null | "chat" | "issue"
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [issueText, setIssueText] = useState("");
  const [issueImage, setIssueImage] = useState(null); // File
  const [issueImagePreview, setIssueImagePreview] = useState(null);
  const [raising, setRaising] = useState(false);
  const [resolveConfirmOpen, setResolveConfirmOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const bottomRef = useRef(null);
  const firstLoad = useRef(true);

  const load = async ({ silent = false } = {}) => {
    try {
      const [{ data: msgs }, { data: state }] = await Promise.all([
        apiClient.get(`/orders/${orderId}/messages`),
        apiClient.get(`/orders/${orderId}/chat-state`),
      ]);
      setMessages(Array.isArray(msgs) ? msgs : []);
      setChatState(state);
      apiClient.post(`/orders/${orderId}/messages/read`).catch(() => {});
    } catch (err) {
      if (!silent) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not open chat");
        navigate(-1);
      }
    } finally {
      if (firstLoad.current) { setLoading(false); firstLoad.current = false; }
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => load({ silent: true }), 3500);
    return () => clearInterval(id);
  }, [orderId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (imageFile) => {
    if (!imageFile && !text.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      if (text.trim()) fd.append("text", text.trim());
      if (imageFile) fd.append("image", imageFile, imageFile.name);
      await apiClient.post(`/orders/${orderId}/messages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Message not sent");
    } finally {
      setSending(false);
    }
  };

  const onPickChatImage = (file) => send(file);

  const onPickIssueImage = (file) => {
    setIssueImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setIssueImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submitIssue = async () => {
    if (!issueText.trim() && !issueImage) { toast.error("Describe the issue or attach a photo"); return; }
    setRaising(true);
    try {
      const fd = new FormData();
      if (issueText.trim()) fd.append("text", issueText.trim());
      if (issueImage) fd.append("image", issueImage, issueImage.name);
      await apiClient.post(`/orders/${orderId}/issues`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Issue raised — chat reopened");
      setRaiseOpen(false);
      setIssueText("");
      setIssueImage(null);
      setIssueImagePreview(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not raise issue");
    } finally {
      setRaising(false);
    }
  };

  const confirmResolveIssue = async () => {
    if (!chatState?.issue?.id) return;
    setResolving(true);
    try {
      await apiClient.post(`/orders/${orderId}/issues/${chatState.issue.id}/resolve`);
      toast.success("Issue Resolved — Delivery Completed");
      setResolveConfirmOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resolve issue");
    } finally {
      setResolving(false);
    }
  };

  const shortRef = useMemo(() => `#${(orderId || "").slice(0, 6).toUpperCase()}`, [orderId]);

  const canSend = !!chatState?.can_send;
  const canRaiseIssue = chatState?.role === "buyer" && chatState?.state === "completed";
  const canResolveIssue = chatState?.is_acceptor && chatState?.state === "issue_open";
  const closedBannerKey = chatState?.state === "completed"
    ? (chatState.order_status === "cancelled" ? "cancelled_completed" : "completed")
    : chatState?.state === "issue_resolved" ? "issue_resolved" : null;
  const closedBanner = closedBannerKey ? CLOSED_BANNER[closedBannerKey] : null;

  return (
    <div className="fixed inset-0 z-[70] bg-slate-50 flex flex-col max-w-md lg:max-w-2xl mx-auto">
      <div
        data-testid="chat-header"
        className="sticky top-0 z-10 bg-white border-b border-slate-100 px-3 py-3 flex items-center gap-2 shadow-sm"
      >
        <button onClick={() => navigate(-1)} data-testid="chat-back-btn" className="w-9 h-9 rounded-full grid place-items-center text-slate-600 hover:bg-slate-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center shrink-0">
          <MessageSquare className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-900">Order Chat</div>
          <div className="text-[11px] text-slate-500 font-mono">{shortRef}</div>
        </div>
        {chatState?.state === "issue_open" && (
          <span data-testid="chat-issue-open-pill" className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100 px-2 py-1 rounded-full flex items-center gap-1 shrink-0">
            <Flag className="w-3 h-3" /> Issue Open
          </span>
        )}
      </div>

      {closedBanner && (
        <div data-testid="chat-closed-banner" className={`px-3 py-2 text-center text-xs font-bold ${closedBanner.tone} flex items-center justify-center gap-1.5`}>
          <Lock className="w-3.5 h-3.5" /> {closedBanner.text}
        </div>
      )}

      <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-3 py-4 space-y-3 pb-40">
        {loading ? (
          <div className="text-center text-slate-400 text-sm py-8">Loading chat...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-8" data-testid="chat-empty">
            No messages yet. Say hello 👋
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            const isIssue = m.type === "issue";
            return (
              <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                  {!mine && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full self-start ${ROLE_TONE[m.sender_role] || "bg-slate-100 text-slate-600"}`}>
                      {m.sender_name} · {ROLE_LABEL[m.sender_role] || m.sender_role}
                    </span>
                  )}
                  <div
                    className={`rounded-2xl px-3 py-2 shadow-sm ${
                      isIssue
                        ? "bg-red-50 border-2 border-red-200 text-red-900 rounded-bl-sm"
                        : mine ? "bg-emerald-600 text-white rounded-br-sm" : "bg-white border border-slate-100 text-slate-900 rounded-bl-sm"
                    }`}
                  >
                    {isIssue && (
                      <div data-testid={`chat-issue-flag-${m.id}`} className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider mb-1">
                        <Flag className="w-3.5 h-3.5" /> Issue Reported
                      </div>
                    )}
                    {m.image_data && (
                      <button onClick={() => setLightbox(m.image_data)} data-testid={`chat-img-${m.id}`} className="block mb-1">
                        <img src={m.image_data} alt="attachment" className="rounded-xl max-h-56 object-cover" />
                      </button>
                    )}
                    {m.content && <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>}
                  </div>
                  <span className="text-[10px] text-slate-400">{timeLabel(m.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div
        data-testid="chat-input-bar"
        className="fixed bottom-[68px] lg:bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md lg:max-w-2xl bg-white border-t border-slate-100 px-3 py-2.5 z-20 space-y-2"
      >
        {(canRaiseIssue || canResolveIssue) && (
          <div className="flex gap-2">
            {canRaiseIssue && (
              <SecondaryButton
                onClick={() => setRaiseOpen(true)}
                data-testid="chat-raise-issue-btn"
                className="flex-1 h-10 text-xs flex items-center justify-center gap-1.5 border-red-200 text-red-700 hover:bg-red-50"
              >
                <Flag className="w-3.5 h-3.5" /> Raise an Issue
              </SecondaryButton>
            )}
            {canResolveIssue && (
              <PrimaryButton
                onClick={() => setResolveConfirmOpen(true)}
                data-testid="chat-resolve-issue-btn"
                className="flex-1 h-10 text-xs flex items-center justify-center gap-1.5"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Issue Resolved
              </PrimaryButton>
            )}
          </div>
        )}
        {canSend ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPickerFor("chat")}
              disabled={sending}
              data-testid="chat-attach-btn"
              className="w-10 h-10 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100 shrink-0 disabled:opacity-50"
              aria-label="Attach image"
            >
              <Camera className="w-5 h-5" />
            </button>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !sending) send(); }}
              placeholder="Type a message..."
              data-testid="chat-text-input"
              className="flex-1 h-10 rounded-full bg-slate-100 px-4 text-sm outline-none focus:ring-2 focus:ring-emerald-300"
            />
            <button
              onClick={() => send()}
              disabled={sending || !text.trim()}
              data-testid="chat-send-btn"
              className="w-10 h-10 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white grid place-items-center shrink-0"
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        ) : (
          <div data-testid="chat-input-disabled-hint" className="h-10 rounded-full bg-slate-100 px-4 flex items-center gap-2 text-xs text-slate-400 font-medium">
            <Lock className="w-3.5 h-3.5" /> This chat is closed — messaging disabled
          </div>
        )}
      </div>

      <ImageSourceSheet
        open={pickerFor === "chat"}
        onClose={() => setPickerFor(null)}
        onFile={onPickChatImage}
      />
      <ImageSourceSheet
        open={pickerFor === "issue"}
        onClose={() => setPickerFor(null)}
        onFile={onPickIssueImage}
      />

      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="chat-lightbox"
        >
          <button onClick={() => setLightbox(null)} data-testid="chat-lightbox-close" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/60 text-white grid place-items-center">
            <X className="w-4 h-4" />
          </button>
          <img src={lightbox} alt="attachment full" className="max-h-[85vh] max-w-full object-contain rounded-xl" />
        </div>
      )}

      {/* Raise an Issue — text, photo, or both. Plain modal (not Radix Dialog):
          Radix's Dialog/Overlay default to z-50, which sits BELOW this page's
          own z-[70] fixed container, so its content was rendered underneath
          the chat page and untappable — root cause of the "can't type" bug. */}
      {raiseOpen && (
        <div
          className="fixed inset-0 z-[85] bg-black/70 flex items-center justify-center p-4"
          onClick={() => { setRaiseOpen(false); setIssueImage(null); setIssueImagePreview(null); }}
          data-testid="raise-issue-dialog"
        >
          <div className="bg-white rounded-3xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Raise an Issue</h3>
              <p className="text-sm text-slate-500 mt-1">
                Describe the problem (e.g. quantity shortage, damaged or incorrect material) and/or attach a photo. This reopens the chat and notifies the supplier.
              </p>
            </div>
            <Textarea
              value={issueText}
              onChange={(e) => setIssueText(e.target.value)}
              placeholder="Describe the issue..."
              rows={4}
              autoFocus
              data-testid="raise-issue-textarea"
              className="rounded-xl text-base"
            />
            {issueImagePreview ? (
              <div className="relative">
                <img src={issueImagePreview} alt="issue attachment" data-testid="raise-issue-preview" className="w-full max-h-40 object-cover rounded-xl border border-slate-200" />
                <button
                  onClick={() => { setIssueImage(null); setIssueImagePreview(null); }}
                  data-testid="raise-issue-remove-photo"
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-black/70 text-white grid place-items-center"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setPickerFor("issue")}
                data-testid="raise-issue-attach-photo"
                className="w-full h-11 rounded-xl border-2 border-dashed border-slate-200 hover:border-red-300 text-slate-500 text-sm font-semibold flex items-center justify-center gap-1.5 transition"
              >
                <ImagePlus className="w-4 h-4" /> Attach a photo (optional)
              </button>
            )}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <SecondaryButton onClick={() => { setRaiseOpen(false); setIssueImage(null); setIssueImagePreview(null); }} data-testid="raise-issue-cancel">Cancel</SecondaryButton>
              <PrimaryButton onClick={submitIssue} loading={raising} data-testid="raise-issue-submit">Submit Issue</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Issue Resolved confirmation — supplier/material provider only, enforced server-side too */}
      {resolveConfirmOpen && (
        <div
          className="fixed inset-0 z-[85] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setResolveConfirmOpen(false)}
          data-testid="resolve-issue-dialog"
        >
          <div className="bg-white rounded-3xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Resolve Issue?</h3>
              <p className="text-sm text-slate-500 mt-1">Are you sure the issue has been resolved?</p>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <SecondaryButton onClick={() => setResolveConfirmOpen(false)} data-testid="resolve-issue-cancel">Cancel</SecondaryButton>
              <PrimaryButton onClick={confirmResolveIssue} loading={resolving} data-testid="resolve-issue-confirm">Yes, Resolve Issue</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
